/**
 * SplitsLayout — Story 2.3-UI (Split-Editor, Referenz splits.html)
 *
 * Layout der Welle-2-Route /splits: Section-Header + zweispaltiges
 * editor-grid (Regel-Liste links, Editor-Spalte rechts als <Outlet/>).
 * Kindrouten:
 *   /splits         -> SplitEditorPlaceholder (Hinweis-Karte)
 *   /splits/create  -> SplitRuleEditor (neue Regel)
 *   /splits/:id     -> SplitRuleEditor (bestehende Regel)
 */
import { Link, Outlet } from '@tanstack/react-router'
import { Trans, useTranslation } from 'react-i18next'
import { SplitRuleList } from './SplitRuleList'
import { PremiumRequired } from '../../components/PremiumRequired'
import { useAccount } from '../settings/useAccount'
import './splits.css'

/**
 * Breadcrumb "Earnings > Commission Rules" (Referenz splits.html:
 * HtLayout.mount breadcrumb: [{label:'Earnings'},{label:'Commission Rules'}]).
 * "Earnings" ist in der Vorlage kein Link (kein href) — reiner Muted-Label,
 * "Commission Rules" ist das aktuelle Blatt.
 */
function SplitsBreadcrumb() {
  const { t } = useTranslation(['splits', 'shell'])
  return (
    <nav className="splits-breadcrumb" aria-label="Breadcrumb" data-testid="splits-breadcrumb">
      <span>{t('shell:nav.sections.income')}</span>
      <span aria-hidden="true">›</span>
      <span className="splits-breadcrumb-current">{t('title')}</span>
    </nav>
  )
}

export function SplitsLayout() {
  const { t } = useTranslation('splits')
  const { account } = useAccount()

  if (account && !account.is_premium) {
    return <PremiumRequired message={t('premiumRequired')} data-testid="splits-premium-required" />
  }

  return (
    <div data-testid="splits-page">
      <SplitsBreadcrumb />
      <div className="section-header">
        <div>
          <h1>{t('title')}</h1>
          <p className="muted">
            <Trans
              i18nKey="splits:subtitle"
              components={{ 1: <Link to="/settlements" /> }}
            />
          </p>
        </div>
        <Link to="/splits/create" className="btn btn-primary" data-testid="splits-new-rule">
          {t('newRule')}
        </Link>
      </div>

      <div className="splits-editor-grid">
        <SplitRuleList />
        <Outlet />
      </div>
    </div>
  )
}

/**
 * Index-Route /splits: reine Übersicht ohne Auswahl. Der Editor öffnet
 * sich erst nach bewusstem Klick auf eine Regel oder "+ New rule" — kein
 * automatisches Preselect der ersten Regel mehr (heliumtracker-web#122:
 * /commissions leitete auf /splits/:id um und öffnete ungefragt den
 * Regel-Editor eines fremden Datensatzes).
 */
export function SplitEditorPlaceholder() {
  const { t } = useTranslation('splits')

  return (
    <div className="card card-pad" data-testid="splits-editor-placeholder">
      <h3 style={{ marginBottom: 4 }}>{t('editor.title')}</h3>
      <p className="muted" style={{ fontSize: 12, margin: '0 0 16px' }}>
        {t('editor.placeholderText')}
      </p>
      <Link to="/splits/create" className="btn">
        {t('list.emptyCta')}
      </Link>
    </div>
  )
}
